import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Product, 
  ProductFilters, 
  ProductCategory
} from '../types';
import { productService } from '../services/productService';
import ProductCard from '../components/ProductCard';
import ProductTable from '../components/ProductTable';
import ProductModal from '../components/ProductModal';
import ProductPreviewModal from '../components/ProductPreviewModal';
import ProductFiltersComponent from '../components/ProductFilters';
import ConfirmationModal from '../components/ConfirmationModal';
import { Search, Plus, Filter, Grid, List, Trash2, ChevronLeft, ChevronRight, AlertTriangle, CheckSquare, Square } from 'lucide-react';
import { customToast } from '../utils/customToast';
import { useTitle } from '../hooks/useTitle';

type ViewMode = 'grid' | 'table';

const ProductsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, isLoading: isAuthLoading, hasModuleAccess } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Permission checks - using useMemo to recalculate when user changes
  const canCreate = React.useMemo(() => hasModuleAccess('products', 'create'), [user, hasModuleAccess]);
  const canEdit = React.useMemo(() => hasModuleAccess('products', 'edit'), [user, hasModuleAccess]);
  const canDelete = React.useMemo(() => hasModuleAccess('products', 'delete'), [user, hasModuleAccess]);
  
  // Debug permissions
  React.useEffect(() => {
    console.log('🔐 ProductsPage Permissions:', {
      role: user?.role,
      permissions: user?.permissions,
      canCreate,
      canEdit,
      canDelete
    });
  }, [user, canCreate, canEdit, canDelete]);
  
  // Set page title
  useTitle('Products');
  
  // State management
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewProduct, setPreviewProduct] = useState<Product | undefined>();
  const [loadingModal, setLoadingModal] = useState(false);
  
  // Filter and pagination
  const [filters, setFilters] = useState<ProductFilters>({
    search: searchParams.get('search') || '',
    category: '',
    status: 'all',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection for bulk operations
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [bulkStatusChange, setBulkStatusChange] = useState<Product['status'] | ''>('');
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });

  // Load data
  const loadproducts = useCallback(async () => {
    // Don't load data if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await productService.getProducts(filters, currentPage, perPage);
      setProducts(response.data);
      setTotal(response.total);
      setTotalPages(response.last_page);
      setCurrentPage(response.current_page);
    } catch (error) {
      console.error('Failed to load products:', error);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, perPage, isAuthLoading, user]);

  const loadCategories = useCallback(async () => {
    // Don't load data if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    try {
      const categoriesData = await productService.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Use fallback categories if API fails
      setCategories([
        { id: 1, name: 'Tablets', description: 'Tablet medications' },
        { id: 2, name: 'Capsules', description: 'Capsule medications' },
        { id: 3, name: 'Syrups', description: 'Liquid medications' },
        { id: 4, name: 'Injections', description: 'Injectable medications' },
        { id: 5, name: 'Ointments', description: 'Topical medications' },
        { id: 6, name: 'Drops', description: 'Eye/Ear drops' }
      ]);
    }
  }, [isAuthLoading, user]);

  useEffect(() => {
    loadproducts();
  }, [loadproducts]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Event handlers
  const handleFiltersChange = (newFilters: ProductFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
      status: 'all',
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
    setCurrentPage(1);
  };

  const handleAddProduct = () => {
    setEditingProduct(undefined);
    setIsModalOpen(true);
  };

  const handleEditProduct = async (Product: Product) => {
    // Open modal immediately
    setEditingProduct(Product);
    setIsModalOpen(true);
    
    try {
      setLoadingModal(true);
      // Fetch the full product with custom fields in background
      const fullProduct = await productService.getProduct(Product.id);
      setEditingProduct(fullProduct);
    } catch (error) {
      customToast.error('Failed to load product details');
      console.error('Failed to load product:', error);
      setIsModalOpen(false);
    } finally {
      setLoadingModal(false);
    }
  };

  const handlePreviewProduct = async (Product: Product) => {
    // Open modal immediately
    console.log('👁️ View button clicked for product:', Product.name, 'ID:', Product.id);
    console.log('📊 Initial product from list:', {
      name: Product.name,
      category: Product.category,
      unit: Product.unit,
      stock: Product.stock_quantity
    });
    
    setPreviewProduct(Product);
    setIsPreviewOpen(true);
    
    try {
      setLoadingModal(true);
      // Fetch the full product with custom fields in background
      console.log('🔄 Fetching full product details for ID:', Product.id);
      const fullProduct = await productService.getProduct(Product.id);
      console.log('✅ Full product fetched successfully:', fullProduct);
      setPreviewProduct(fullProduct);
    } catch (error) {
      customToast.error('Failed to load product details');
      console.error('❌ Failed to load product:', error);
      setIsPreviewOpen(false);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Product',
      message: 'Are you sure you want to delete this Product? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await productService.deleteProduct(id);
          customToast.success('Product deleted successfully!');
          loadproducts();
          // Remove from selection if it was selected
          setSelectedProducts(prev => prev.filter(m => m.id !== id));
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error('Failed to delete Product:', error);
          customToast.error('Failed to delete Product. Please try again.');
        }
      }
    });
  };

  const handleSelectProduct = (Product: Product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(m => m.id === Product.id);
      if (isSelected) {
        return prev.filter(m => m.id !== Product.id);
      } else {
        return [...prev, Product];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    
    setConfirmationModal({
      isOpen: true,
      title: 'Delete products',
      message: `Are you sure you want to delete ${selectedProducts.length} selected Product(s)? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          const ids = selectedProducts.map(m => m.id);
          await productService.bulkDelete(ids);
          customToast.success(`Successfully deleted ${selectedProducts.length} Product(s)!`);
          setSelectedProducts([]);
          loadproducts();
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error('Failed to delete products:', error);
          customToast.error('Failed to delete selected products. Please try again.');
        }
      }
    });
  };

  const handleBulkStatusUpdate = async (status: Product['status']) => {
    if (selectedProducts.length === 0) return;

    try {
      const ids = selectedProducts.map(m => m.id);
      await productService.bulkUpdateStatus(ids, status);
      setSelectedProducts([]);
      loadproducts();
    } catch (error) {
      console.error('Failed to update status:', error);
      setError('Failed to update Product status. Please try again.');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Stats calculation
  const stats = {
    total: total,
    lowStock: products.filter(m => m.stock_quantity <= 10).length, // Use fixed threshold of 10
    expired: products.filter(m => m.expiry_date && new Date(m.expiry_date) < new Date()).length,
    expiringSoon: products.filter(m => {
      if (!m.expiry_date) return false;
      const expiry = new Date(m.expiry_date);
      const today = new Date();
      const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
    }).length
  };

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Products
            </h1>
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Manage your grocery store's product inventory and stock.
            </p>
          </div>
          
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-3">
            {/* View Mode Toggle */}
            <div className={`flex items-center border rounded-xl p-1 transition-all duration-200 ${
              theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'
            }`}>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                  viewMode === 'table'
                    ? 'bg-blue-500 text-white shadow-md'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Table View"
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                  viewMode === 'grid'
                    ? 'bg-blue-500 text-white shadow-md'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <Grid size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:space-x-3 sm:gap-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 hover:border-gray-500'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
                }`}
                title={showFilters ? 'Hide Filters' : 'Show Filters'}
              >
                <Filter size={16} />
                <span className="text-sm">Filters</span>
              </button>
              
              {canCreate && (
                <button
                  onClick={handleAddProduct}
                  className="flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation"
                  title="Add New Product"
                >
                  <Plus size={16} />
                  <span className="text-sm">Add Product</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search size={16} className={`absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`} />
          <input
            type="text"
            placeholder="Search products..."
            value={filters.search}
            onChange={(e) => handleFiltersChange({ ...filters, search: e.target.value })}
            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-750'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-gray-50'
            } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20`}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total products</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
              <Plus className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Low Stock</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold text-yellow-600`}>{stats.lowStock}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg flex-shrink-0">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Expiring Soon</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold text-orange-600`}>{stats.expiringSoon}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-orange-100 rounded-lg flex-shrink-0">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Expired</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold text-red-600`}>{stats.expired}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-red-100 rounded-lg flex-shrink-0">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <ProductFiltersComponent
          filters={filters}
          categories={categories}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          loading={loading}
        />
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedProducts.length > 0 && (canEdit || canDelete) && (
        <div className={`bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 sm:p-4 mb-4 border border-blue-200 dark:border-blue-700`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center">
              <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2" />
              <span className={`text-sm sm:text-base font-medium ${theme === 'dark' ? 'text-blue-300' : 'text-blue-800'}`}>
                {selectedProducts.length} Product(s) selected
              </span>
            </div>
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              {canEdit && (
                <select
                  value={bulkStatusChange}
                  onChange={(e) => setBulkStatusChange(e.target.value as Product['status'] | '')}
                  className={`px-3 py-2 rounded-lg border text-sm touch-manipulation ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Change Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              )}
              
              <div className="flex space-x-2 w-full sm:w-auto">
                {canEdit && bulkStatusChange && (
                  <button
                    onClick={() => {
                      handleBulkStatusUpdate(bulkStatusChange);
                      setBulkStatusChange('');
                    }}
                    className="flex-1 sm:flex-none px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors touch-manipulation"
                  >
                    Apply
                  </button>
                )}
                
                {canDelete && (
                  <button
                    onClick={handleBulkDelete}
                    className="flex-1 sm:flex-none px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center touch-manipulation"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Selected
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'table' ? (
        <ProductTable
          products={products}
          onEdit={canEdit ? handleEditProduct : undefined}
          onDelete={canDelete ? handleDeleteProduct : undefined}
          onSelect={(canEdit || canDelete) ? handleSelectProduct : undefined}
          selectedProducts={selectedProducts}
          onSelectAll={(canEdit || canDelete) ? handleSelectAll : undefined}
          onPreview={handlePreviewProduct}
          loading={loading}
        />
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {/* Grid View Header with Select All */}
          {products.length > 0 && (canEdit || canDelete) && (
            <div className={`flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 p-3 rounded-lg border ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center touch-manipulation"
                >
                  {selectedProducts.length === products.length && products.length > 0 ? (
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Select All ({products.length} items)
                </span>
              </div>
              {selectedProducts.length > 0 && (
                <span className={`text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                  {selectedProducts.length} selected
                </span>
              )}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className={`p-4 sm:p-6 rounded-lg border animate-pulse ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                {/* Header: Name + Status Badge + Checkbox */}
                <div className="flex items-start justify-between mb-3 sm:mb-4 min-w-0">
                  <div className="flex-1 min-w-0 pr-2 space-y-2">
                    {/* Name + status badge row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`h-5 w-32 rounded ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-5 w-16 rounded-full ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                    </div>
                  </div>
                  {/* Checkbox */}
                  <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded flex-shrink-0 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`} />
                </div>

                {/* Brand */}
                <div className={`h-3 w-24 rounded mb-3 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />

                {/* Category */}
                <div className={`h-3 w-28 rounded mb-3 sm:mb-4 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />

                {/* Stock Info Grid (2 columns on mobile, 2 on tablet/desktop) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div>
                    <div className={`h-3 w-20 rounded mb-2 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-5 w-24 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                  <div>
                    <div className={`h-3 w-20 rounded mb-2 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-5 w-24 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                </div>

                {/* Price Info Grid (2 on mobile, 3 on desktop) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4 text-sm">
                  <div>
                    <div className={`h-3 w-20 rounded mb-2 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-5 w-20 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                  <div>
                    <div className={`h-3 w-20 rounded mb-2 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-6 w-24 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                  <div className="hidden sm:block">
                    <div className={`h-3 w-20 rounded mb-2 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-5 w-16 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                </div>

                {/* Description */}
                <div className="mb-3 sm:mb-4 space-y-1.5">
                  <div className={`h-3 w-full rounded ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`} />
                  <div className={`h-3 w-4/5 rounded ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`} />
                </div>

                {/* Action Buttons (View, Edit, Delete) */}
                <div className="flex flex-col gap-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex gap-2">
                    <div className={`flex-1 h-9 sm:h-10 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`flex-1 h-9 sm:h-10 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`flex-1 h-9 sm:h-10 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                </div>
              </div>
            ))
          ) : products.length > 0 ? (
            products.map(Product => (
              <ProductCard
                key={Product.id}
                Product={Product}
                onEdit={canEdit ? handleEditProduct : undefined}
                onDelete={canDelete ? handleDeleteProduct : undefined}
                onSelect={(canEdit || canDelete) ? handleSelectProduct : undefined}
                onPreview={handlePreviewProduct}
                isSelected={selectedProducts.some(m => m.id === Product.id)}
                loading={loadingModal}
              />
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center h-48 sm:h-64">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <Plus className={`w-6 h-6 sm:w-8 sm:h-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <h3 className={`text-lg sm:text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                No products found
              </h3>
              <p className={`text-sm mb-3 sm:mb-4 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {canCreate ? 'Add your first Product to get started' : 'No products available'}
              </p>
              {canCreate && (
                <button
                  onClick={handleAddProduct}
                  className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation"
                >
                  Add Product
                </button>
              )}
            </div>
          )}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 mt-6 sm:mt-8">
          <p className={`text-sm order-2 sm:order-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, total)} of {total} products
          </p>
          
          <div className="flex items-center space-x-1 sm:space-x-2 order-1 sm:order-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl border text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md touch-manipulation ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Previous</span>
              <ChevronLeft className="w-4 h-4 sm:hidden" />
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              const isCurrentPage = page === currentPage;
              
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`min-w-[36px] sm:min-w-[40px] h-9 sm:h-10 flex items-center justify-center rounded-xl text-sm transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation ${
                    isCurrentPage
                      ? 'bg-blue-500 text-white shadow-lg'
                      : theme === 'dark'
                        ? 'border border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700'
                        : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl border text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md touch-manipulation ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4 sm:hidden" />
            </button>
          </div>
        </div>
      )}

      {/* Product Modal */}
      <ProductModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={editingProduct}
        onProductUpdated={loadproducts}
        isLoading={loadingModal}
      />

      {/* Product Preview Modal */}
      {previewProduct && (
        <ProductPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          product={previewProduct}
          isLoading={loadingModal}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />
    </div>
  );
};

export default ProductsPage;